// Enums core (Dim/Modality/DictamenState) están en primitives.ts;
// acá van constantes pedagógicas que dependen de ellos.
import { z } from 'zod';
export const ALL_DIMS = [
    'recuperacion',
    'comprension',
    'analisis',
    'transferencia',
];
export const DIM_LABEL = {
    recuperacion: 'Recuperación',
    comprension: 'Comprensión',
    analisis: 'Análisis',
    transferencia: 'Transferencia',
};
export const HELP_LABEL = {
    aclaracion: 'Aclaración (vocabulario)',
    reformulacion: 'Reformular pregunta',
    pista_parcial: 'Pista parcial',
    pista_significativa: 'Ayuda conceptual',
};
/** Penalizaciones por tipo de ayuda — P4 §5.3 + DEC-11. */
export const HELP_PENALTY = {
    aclaracion: 0,
    reformulacion: -5,
    pista_parcial: -10,
    pista_significativa: -20,
};
export const MODALITY_LABEL = {
    written: 'Escrita',
    oral: 'Oral',
    mixed: 'Mixta',
    simplified_1_3: '1°-3° Básico',
};
/**
 * Máximo por modalidad, minutos. `mixed=30` es solo el tope inicial visible
 * (asume 50/50); el budget real sale de `computeEffectiveBudgetSec`.
 */
export const MODALITY_MAX_MIN = {
    written: 40,
    oral: 20,
    mixed: 30,
    simplified_1_3: 15,
};
/**
 * Mixed: tiempo = (% oral × 20 min) + (% escrito × 40 min) (spec §3).
 * Pre-primer-turno asume 50/50 para que el cronómetro SPA arranque visible.
 */
export function computeEffectiveBudgetSec(modality, oralSecondsUsed, textSecondsUsed) {
    if (modality !== 'mixed')
        return MODALITY_MAX_MIN[modality] * 60;
    const total = oralSecondsUsed + textSecondsUsed;
    if (total === 0)
        return MODALITY_MAX_MIN.mixed * 60;
    const oralShare = oralSecondsUsed / total;
    const textShare = textSecondsUsed / total;
    return Math.round(oralShare * 20 * 60 + textShare * 40 * 60);
}
export const OA_TIPO_LABEL = {
    conceptual: 'Conceptual',
    procedural: 'Procedimental',
    argumentative: 'Argumentativo',
    investigative: 'Investigativo',
};
export const DICTAMEN_LABEL = {
    no_evidencia: 'No evidencia dominio',
    en_desarrollo: 'En desarrollo',
    suficiente: 'Dominio suficiente',
    profundo: 'Dominio profundo',
};
/**
 * Snapshot curricular actual — se persiste en evaluation_session para poder
 * reproducir un dictamen con las mismas reglas que se aplicaron el día que
 * corrió. Cambiar de valor SOLO cuando el catálogo Mineduc publica una
 * nueva base curricular.
 */
export const CURRICULUM_VERSION = 'mineduc-2024';
/** Fallback por defecto cuando el body de creación no distingue el tipo. */
export const DEFAULT_OA_TIPO = 'conceptual';
// Multi-OA por pilot (2026-08): el profe elige uno o varios OAs del hito.
// La matriz + interview los tratan como conjunto (rúbrica combinada). El
// dictamen sigue siendo uno solo, agregado. Cap 10 para no explotar prompts.
export const MAX_OAS_PER_PILOT = 10;
export const PilotOaSchema = z.object({
    oa_codigo: z.string().min(1).max(80),
    oa_descripcion: z.string().min(1).max(2000),
    tipo: z
        .enum(['conceptual', 'procedural', 'argumentative', 'investigative'])
        .default(DEFAULT_OA_TIPO),
});
export const PilotOasArraySchema = z
    .array(PilotOaSchema)
    .min(1)
    .max(MAX_OAS_PER_PILOT);
// Consideraciones libres del profesor para el LLM ("preguntas en inglés B1",
// "adaptar tono para NEE", "enfocarse en X sub-tema"). 500 chars balancea
// expresividad vs tokens del prompt cacheado. El texto se inyecta tal cual
// en matrixGenerator + interview engine bajo header explícito.
export const TEACHER_HINT_MAX_LEN = 500;
export const TeacherHintSchema = z.string().max(TEACHER_HINT_MAX_LEN).optional();
/**
 * Tope operacional de alumnos por creación de aula. Post-paralelización con
 * CONCURRENCY=10 en createStudentsForClassroom, 60 alumnos cierran la request
 * en ~5-8s (antes el for-await serial daba 15-20s con 40). Subirlo más allá
 * requiere validar contra el pool de PG (PG_POOL_MAX=50) y contra rate limits
 * de Pioneros API si el context lookup se dispara.
 */
export const MAX_STUDENTS_PER_CLASSROOM = 60;
/** Longitud máxima para nombres visibles (aulas, alumnos, títulos). */
export const NAME_MAX_LEN = 200;
/**
 * Roster de alumnos por nivel/sede. Un curso Mineduc raramente supera 45
 * alumnos; 200 cubre "multi-curso del mismo nivel" sin overshoot. Si el
 * request llega al tope, el SPA muestra "hay más — refina búsqueda".
 */
export const ROSTER_MAX_RESULTS = 200;
/**
 * Polling de la vista live del aula. 4s balancea "feedback razonable para el
 * docente" vs "carga contra pgPool". Al escalar a >20 aulas activas simultáneas
 * en una sede, considerar SSE + cache TTL corto en getClassroomLive.
 */
export const CLASSROOM_LIVE_POLL_MS = 4000;
/** Backoff tras N errores consecutivos en polling (evita hammering al server). */
export const CLASSROOM_LIVE_POLL_STOP_AFTER_ERRORS = 3;
// ---------------------------------------------------------------------------
// Tipo de evaluación (Pioneros `core.milestone.evaluation_type`)
// ---------------------------------------------------------------------------
// Los tres valores salen de la data real de Pioneros, no los inventamos:
//   Mastery check                 3005 hitos
//   Evaluación de cierre de ruta   395
//   Evaluación de progreso         344
//
// Define cuánta evidencia pedir. SIEMPRE se recorren las 4 dimensiones
// Marzano — es el núcleo del modelo y no se recorta por tipo; lo que varía es
// la profundidad con que se evalúa cada una.
export const EVALUATION_TYPES = [
    'mastery_check',
    'progress',
    'route_close',
];
/** Etiquetas tal como las escribe Pioneros, para mapear desde el ETL. */
export const PIONEROS_EVALUATION_TYPE = {
    'Mastery check': 'mastery_check',
    'Evaluación de progreso': 'progress',
    'Evaluación de cierre de ruta': 'route_close',
};
export const EVALUATION_TYPE_LABEL = {
    mastery_check: 'Mastery check',
    progress: 'Evaluación de progreso',
    route_close: 'Evaluación de cierre de ruta',
};
// Antes era 4 por dimensión (16 evidencias) para todo, sin mirar el tipo: un
// mastery check de un OA puntual pedía lo mismo que un cierre de ruta. De ahí
// las sesiones de 37 minutos que las docentes reportaron como insostenibles
// para un niño de 2° básico.
export const EVALUATION_TYPE_PARAMS = {
    mastery_check: { evidencesPerDim: 1, budgetSec: 8 * 60, expectedMinutesLabel: '5 a 8 minutos' },
    progress: { evidencesPerDim: 2, budgetSec: 15 * 60, expectedMinutesLabel: '10 a 15 minutos' },
    route_close: { evidencesPerDim: 3, budgetSec: 30 * 60, expectedMinutesLabel: '20 a 30 minutos' },
};
/** Sin hito resuelto caemos al intermedio, no al más largo. */
export const DEFAULT_EVALUATION_TYPE = 'progress';
/** Tipo congelado en el snapshot del hito; `undefined` en sesiones anteriores al cambio. */
export function evaluationTypeFromSnapshot(snapshot) {
    const t = snapshot?.evaluation_type;
    return typeof t === 'string' && EVALUATION_TYPES.includes(t)
        ? t
        : undefined;
}
/**
 * Tiempo con el que arranca la sesión. Manda el tipo de actividad; la modalidad
 * solo pone un techo (p. ej. 1°-3° nunca pasa de 15 min). Se persiste en
 * `initial_budget_sec`, así una sesión ya emitida no cambia de reglas.
 */
export function initialBudgetSecFor(type, modality) {
    const byType = EVALUATION_TYPE_PARAMS[type ?? DEFAULT_EVALUATION_TYPE].budgetSec;
    return Math.min(byType, MODALITY_MAX_MIN[modality] * 60);
}
/**
 * Presupuesto vigente de una sesión en curso: el inicial persistido, acotado por
 * la mezcla real oral/escrito en mixta. Lo usan servidor y cliente para que el
 * cronómetro del alumno y el cierre duro coincidan.
 */
export function effectiveBudgetCapSec(initialBudgetSec, modality, oralSecondsUsed, textSecondsUsed) {
    return Math.min(initialBudgetSec, computeEffectiveBudgetSec(modality, oralSecondsUsed, textSecondsUsed));
}
/** Fallback cuando el hito no trae criterio. Es el 80 que `scoring.ts` tenía fijo. */
export const DEFAULT_MASTERY_THRESHOLD = 80;
/** Umbral del hito congelado en el snapshot al crear la sesión. Ausente = default. */
export function masteryThresholdFromSnapshot(snapshot) {
    const t = snapshot?.mastery_threshold;
    return typeof t === 'number' && t > 0 && t <= 100 ? t : DEFAULT_MASTERY_THRESHOLD;
}
/**
 * Cortes de los estados del dictamen, derivados del umbral del hito. Los otros
 * dos se escalan desde él para que las cuatro bandas conserven su relación
 * (con umbral 80: 50 / 80 / 95). Lo usan el cálculo del estado en el servidor y
 * las etiquetas por área del alumno, para que nunca se contradigan.
 */
export function masteryBands(threshold) {
    const sufficient = Math.max(1, Math.min(100, threshold));
    return {
        noEvidence: sufficient * 0.625,
        sufficient,
        deep: sufficient + (100 - sufficient) * 0.75,
    };
}
/** Nivel de una dimensión frente al umbral del hito: domina, va bien o a practicar. */
export function dimensionLevel(score, threshold) {
    const b = masteryBands(threshold);
    if (score >= b.sufficient)
        return 'strong';
    if (score >= b.noEvidence)
        return 'ok';
    return 'work';
}
/**
 * `mastery_criteria` viene como "80%" / "90%". Pioneros lo define hito por
 * hito y va de 40% a 100%; Marzano tenía 80 hardcodeado para todo.
 */
export function parseMasteryThreshold(raw) {
    if (!raw)
        return null;
    const m = /(\d{1,3})\s*%?/.exec(raw.trim());
    if (!m)
        return null;
    const n = Number(m[1]);
    return Number.isFinite(n) && n > 0 && n <= 100 ? n : null;
}
//# sourceMappingURL=marzano.js.map